import { useState } from 'react';
import { Building2, Check, ClipboardCopy, ShieldCheck, ShieldAlert } from 'lucide-react';
import type { EntrepriseTrouvee } from '../services/api';

import { dateFr } from '../utils/dates';
/**
 * Fiche d'identité légale d'une entreprise, en lecture seule.
 *
 * Les informations viennent du répertoire Sirene et des référentiels de
 * l'INSEE : elles ne se corrigent pas ici. Une erreur se règle auprès de
 * l'INSEE, pas dans ce formulaire — d'où l'absence de champs modifiables.
 *
 * `onReprendreNom` fait exception : la raison sociale officielle est
 * précisément ce que l'on veut voir figurer sur le devis, et la recopier à la
 * main invite la faute de frappe.
 */
export default function CarteEntreprise({
  entreprise,
  onReprendreNom,
}: {
  entreprise: EntrepriseTrouvee;
  onReprendreNom?: (nom: string) => void;
}) {
  const [repris, setRepris] = useState(false);

  const lignes: { label: string; valeur: string | null }[] = [
    { label: 'Raison sociale', valeur: entreprise.nom },
    { label: 'Activité', valeur: entreprise.activite },
    {
      label: 'Activité (code NAF)',
      valeur: entreprise.codeNaf,
    },
    {
      label: 'Adresse',
      valeur: entreprise.adresse,
    },
    { label: 'Forme juridique', valeur: entreprise.formeJuridique },
    {
      label: 'Première immatriculation le',
      valeur: entreprise.dateImmatriculation
        ? dateFr(entreprise.dateImmatriculation)
        : null,
    },
    { label: 'N° TVA intracommunautaire', valeur: entreprise.tvaIntracommunautaire },
  ];

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-4 space-y-3">
      <div className="flex items-start gap-2">
        <Building2 className="w-5 h-5 text-primary-600 dark:text-primary-400 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900 dark:text-gray-100">
            Informations officielles de l&apos;entreprise
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Répertoire Sirene et référentiels INSEE — non modifiables.
          </p>
        </div>
        {entreprise.active ? (
          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-green-100 text-green-800 shrink-0">
            <ShieldCheck className="w-3.5 h-3.5" /> En activité
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-red-100 text-red-800 shrink-0">
            <ShieldAlert className="w-3.5 h-3.5" /> Cessée
          </span>
        )}
      </div>

      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
        {lignes.map(({ label, valeur }) => {
          const reprenable = label === 'Raison sociale' && valeur && onReprendreNom;

          return (
            <div key={label} className="min-w-0">
              <dt className="text-xs text-gray-500 dark:text-gray-400">{label}</dt>
              <dd className="text-gray-900 dark:text-gray-100 break-words flex items-start gap-1.5">
                <span className="min-w-0">
                  {valeur ?? <span className="text-gray-400">Non renseigné</span>}
                </span>

                {reprenable && (
                  <button
                    type="button"
                    title="Reprendre cette raison sociale comme nom du sponsor"
                    onClick={() => {
                      onReprendreNom(valeur);
                      setRepris(true);
                      setTimeout(() => setRepris(false), 2000);
                    }}
                    className="shrink-0 text-gray-400 hover:text-primary-600 mt-0.5"
                  >
                    {repris ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <ClipboardCopy className="w-4 h-4" />
                    )}
                  </button>
                )}
              </dd>
            </div>
          );
        })}
      </dl>

      {entreprise.tvaIntracommunautaire && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Le numéro de TVA est déduit du SIREN selon la règle officielle. Il ne s&apos;applique
          qu&apos;aux entreprises françaises assujetties à la TVA.
        </p>
      )}
    </div>
  );
}
